import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ClinicalReportSection from "./ClinicalReportSection";
import { diagnosticReportConfig } from "./abhaClinicalConfigs";
import { getPatientRadiologyReports } from "../../../../store/slices/radiologyReport";
import { getPatientLabReports } from "../../../../store/slices/labReportSlice";

const normalizeDiagnosticStage = (statusValue) => {
  const normalized = String(statusValue || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");

  if (normalized.includes("final") || normalized.includes("complete")) {
    return "Final";
  }
  return "In Progress";
};

const DiagnosticReportSection = ({ patientId, patientData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { token } = useSelector((state) => state.auth);
  const { clinicInfo } = useSelector((state) => state.clinic);
  const {
    patientRadiologyReports,
    patientLoading,
    patientRadiologyReportsForPatientId,
  } = useSelector(
    (state) => state.radiologyReport,
  );
  const {
    patientLabReports,
    patientLabReportsLoading,
    patientLabReportsForPatientId,
  } = useSelector(
    (state) => state.labReport,
  );

  const clinicId = clinicInfo?.[0]?.clinicId || null;

  useEffect(() => {
    if (!token || !patientId || !clinicId) return;
    dispatch(
      getPatientRadiologyReports({
        patientId,
        clinicId,
        token,
        limit: 30,
      }),
    );
    dispatch(
      getPatientLabReports({
        patientId,
        clinicId,
        token,
        limit: 30,
      }),
    );
  }, [dispatch, token, patientId, clinicId]);

  const externalRows = useMemo(() => {
    const requestedPatientId = String(patientId || "");
    const scopedRadiology =
      String(patientRadiologyReportsForPatientId || "") === requestedPatientId
        ? patientRadiologyReports
        : [];
    const scopedLab =
      String(patientLabReportsForPatientId || "") === requestedPatientId
        ? patientLabReports
        : [];

    const diagnosticRows = (Array.isArray(scopedRadiology)
      ? scopedRadiology
      : []
    ).map((report) => ({
      sourceStage: normalizeDiagnosticStage(report?.labReportStatus),
      _id: `source-rad-${report?._id || ""}`,
      consultationDate: report?.appDate || "",
      service: report?.serviceId || "",
      serviceName: report?.serviceName || "",
      doctor: report?.assignedPhysicianId || "",
      doctorName: report?.assignedPhysicianName || "",
      appointmentId: String(report?.appointmentId || ""),
      reportTitle: report?.serviceName
        ? `${report.serviceName} Report`
        : "Diagnostic Report",
      remark:
        String(report?.remark || "").trim() ||
        `${report?.serviceName || "Diagnostic"} report available.`,
      sourceStatusText: `Not Linked (${normalizeDiagnosticStage(report?.labReportStatus)})`,
      sourceModule: "radiology",
      sourceRecordId: String(report?._id || ""),
      sourceAppointmentId: String(report?.appointmentId || ""),
    }));

    const labRows = (Array.isArray(scopedLab) ? scopedLab : []).map((report) => {
      const testSummary = (report?.tests || [])
        .slice(0, 3)
        .map((test) => `${test?.testName || "Test"}${test?.value ? `: ${test.value}` : ""}`)
        .join(" | ");
      const profileSummary = (report?.profiles || [])
        .slice(0, 3)
        .map(
          (profile) =>
            `${profile?.profileName || "Profile"}${
              profile?.value ? `: ${profile.value}` : ""
            }`,
        )
        .join(" | ");

      return {
        sourceStage: normalizeDiagnosticStage(report?.labReportStatus),
        _id: `source-lab-${report?._id || ""}`,
        consultationDate: report?.appDate || "",
        service: report?.serviceId || "",
        serviceName: report?.serviceName || "",
        doctor: report?.assignedPhysicianId || "",
        doctorName: report?.assignedPhysicianName || "",
        appointmentId: String(report?.appointmentId || ""),
        reportTitle: report?.serviceName
          ? `${report.serviceName} Lab Report`
          : "Pathology Report",
        remark:
          [testSummary, profileSummary].filter(Boolean).join(" | ") ||
          "Pathology report available.",
        sourceStatusText: `Not Linked (${normalizeDiagnosticStage(report?.labReportStatus)})`,
        sourceModule: "lab",
        sourceRecordId: String(report?._id || ""),
        sourceAppointmentId: String(report?.appointmentId || ""),
      };
    });

    return [...diagnosticRows, ...labRows];
  }, [
    patientId,
    patientRadiologyReportsForPatientId,
    patientLabReportsForPatientId,
    patientRadiologyReports,
    patientLabReports,
  ]);

  const handleExternalView = useCallback(
    (record) => {
      if (!record) return;

      sessionStorage.setItem("allReportsId", String(record?.sourceRecordId || ""));
      sessionStorage.removeItem("outSourceToId");
      sessionStorage.removeItem("outSourceFromId");
      sessionStorage.setItem("patientId", String(patientId || ""));
      sessionStorage.setItem(
        "appointmentId",
        String(record?.sourceAppointmentId || ""),
      );

      if (record?.sourceModule === "radiology") {
        navigate("/viewRadiologyReport");
        return;
      }

      navigate("/labReportPdf");
    },
    [navigate, patientId],
  );

  return (
    <ClinicalReportSection
      patientId={patientId}
      patientData={patientData}
      config={diagnosticReportConfig}
      externalRows={externalRows}
      externalLoading={patientLoading || patientLabReportsLoading}
      onExternalView={handleExternalView}
    />
  );
};

export default DiagnosticReportSection;
